// useNodeEnumPills.js

import { useMemo } from 'react';
import { isEnumCompatibleFieldType } from 'utilities/FieldTypes';

// Utilities
import { getEnumOptionColor, getEnumOptionIconKey, getEnumOptionLabel } from 'utilities/EnumOptions';
import { getFieldLabel } from 'utilities/NodeLabels';
import { getResolvedFields } from 'utilities/NodeType';

/** Max freeform tag pills in the header strip before a +N overflow pill. */
const MAX_VISIBLE_TAG_PILLS = 5;

function normalizeEnumRawValue(rawValue) {
  if (rawValue == null || rawValue === '') return '';
  if (typeof rawValue === 'object' && !Array.isArray(rawValue)) {
    return String(rawValue.value || rawValue.key || '').trim();
  }
  return String(rawValue).trim();
}

/**
 * Unique trimmed tags from node.tags (case-insensitive dedupe, keep first casing).
 *
 * @param {object|null|undefined} node
 * @returns {string[]}
 */
function listUniqueNodeTags(node) {
  const rawTags = Array.isArray(node?.tags) ? node.tags : [];
  const seen = new Set();
  const unique = [];

  rawTags.forEach((tag) => {
    const label = String(tag || '').trim();
    if (!label) return;
    const key = label.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(label);
  });

  return unique;
}

/**
 * Build enum/multiselect/tags pills for a node's metadata strip.
 *
 * @param {object} args
 * @param {object|null} args.node
 * @param {boolean} [args.nodeTypesReady=true]
 * @param {string} [args.language='en']
 * @param {boolean} [args.includeEmpty=false] When true, include unset single-select
 *   enum fields as placeholder pills (e.g. "Status…"). Tags never get empty placeholders.
 */
export default function useNodeEnumPills({
  node,
  nodeTypesReady = true,
  language = 'en',
  includeEmpty = false,
}) {
  return useMemo(() => {
    if (!nodeTypesReady || !node?.type) return [];

    const fields = getResolvedFields(node.type, { includeHidden: false, excludeSecured: true });
    const safeFields = Array.isArray(fields) ? fields : [];
    const pills = [];

    safeFields.forEach((field) => {
      if (!field || (!isEnumCompatibleFieldType(field) && field.type !== 'multiselect') || !field.name) return;
      // Utility enums (e.g. unit) can opt out of the header pill strip.
      if (field.displayAsPill === false) return;

      const sourceKey = String(field.source || node.type || '').toLowerCase();
      const dataKey = sourceKey ? `data_${sourceKey}` : '';
      const rawValue = dataKey && node?.[dataKey]?.[field.name] !== undefined
        ? node?.[dataKey]?.[field.name]
        : undefined;

      const fieldLabel = String(getFieldLabel(field, language) || field.name || '').trim();
      const placeholder = fieldLabel ? `${fieldLabel}…` : 'Select…';
      const isEnum = isEnumCompatibleFieldType(field);
      const editable = isEnum && field.writable !== false;

      if (isEnum) {
        const strValue = normalizeEnumRawValue(rawValue);
        if (!strValue) {
          if (!includeEmpty) return;
          pills.push({
            key: `${field.name}-empty`,
            field,
            fieldName: field.name,
            fieldType: field.type || 'enum',
            value: '',
            label: placeholder,
            placeholder,
            color: '',
            iconKey: '',
            isEmpty: true,
            editable,
          });
          return;
        }

        const label = getEnumOptionLabel(field.options || [], strValue, language);
        if (!label || !String(label).trim()) return;

        pills.push({
          key: `${field.name}-${strValue}`,
          field,
          fieldName: field.name,
          fieldType: field.type || 'enum',
          value: strValue,
          label,
          placeholder,
          color: getEnumOptionColor(field.options || [], strValue),
          iconKey: getEnumOptionIconKey(field.options || [], strValue),
          isEmpty: false,
          editable,
        });
        return;
      }

      // Multiselect: read-only pills for selected values only (v1).
      const values = Array.isArray(rawValue) ? rawValue : [rawValue];
      values.forEach((value, idx) => {
        const strValue = normalizeEnumRawValue(value);
        if (!strValue) return;

        const label = getEnumOptionLabel(field.options || [], strValue, language);
        if (!label || !String(label).trim()) return;

        pills.push({
          key: `${field.name}-${strValue}-${idx}`,
          field,
          fieldName: field.name,
          fieldType: 'multiselect',
          value: strValue,
          label,
          placeholder,
          color: getEnumOptionColor(field.options || [], strValue),
          iconKey: getEnumOptionIconKey(field.options || [], strValue),
          isEmpty: false,
          editable: false,
        });
      });
    });

    // Tags: freeform labels on node.tags — read-only strip, only when present.
    // Opt out with displayAsPill: false on a schema tags field.
    const tagsField = safeFields.find((field) => field?.type === 'tags' && field.name) || null;
    const tagsOptedOut = Boolean(tagsField && tagsField.displayAsPill === false);
    const uniqueTags = tagsOptedOut ? [] : listUniqueNodeTags(node);

    if (uniqueTags.length > 0) {
      const resolvedTagsField = tagsField || { name: 'tags', type: 'tags' };
      const visibleTags = uniqueTags.slice(0, MAX_VISIBLE_TAG_PILLS);
      const overflowCount = uniqueTags.length - visibleTags.length;

      visibleTags.forEach((label, idx) => {
        pills.push({
          key: `tags-${label.toLowerCase()}-${idx}`,
          field: resolvedTagsField,
          fieldName: resolvedTagsField.name,
          fieldType: 'tags',
          value: label,
          label,
          placeholder: '',
          color: '',
          iconKey: '',
          isEmpty: false,
          editable: false,
        });
      });

      if (overflowCount > 0) {
        const overflowLabels = uniqueTags.slice(MAX_VISIBLE_TAG_PILLS);
        pills.push({
          key: 'tags-overflow',
          field: resolvedTagsField,
          fieldName: resolvedTagsField.name,
          fieldType: 'tags',
          value: '',
          label: `+${overflowCount}`,
          title: overflowLabels.join(', '),
          placeholder: '',
          color: '',
          iconKey: '',
          isEmpty: false,
          editable: false,
        });
      }
    }

    return pills;
  }, [includeEmpty, language, node, nodeTypesReady]);
}
